import { useDebounceValue } from "usehooks-ts";

import { getMaxSlippageFactor } from "#/config/slippage";
import { useThrottle } from "#/hooks/useThrottle";
import { useMarketIndexPrice } from "#/store/app/indexPrices/marketIndexPrice";
import { usePerpsOrderbook } from "#/store/perps/page/perpsOrderbook";
import { usePerpIsBuy } from "#/store/perps/tradePanel/perpIsBuy";
import { usePerpLimitPrice } from "#/store/perps/tradePanel/perpLimitPrice";
import { formatValue } from "#/utils/valueFormatters";

export const useValidatePerpLimitPrice = () => {
  const { perpIsBuy } = usePerpIsBuy();
  const { perpsOrderbook } = usePerpsOrderbook();
  const { perpLimitPrice } = usePerpLimitPrice();
  const { marketIndexPrice } = useMarketIndexPrice();
  const [debouncedPerpLimitPrice] = useDebounceValue(perpLimitPrice, 800);
  const throttledIndexPrice = useThrottle(marketIndexPrice, 800);

  if (!debouncedPerpLimitPrice) return { limitPriceError: "" };

  const limitPriceNum = Number(debouncedPerpLimitPrice);
  const indexPriceNum = Number(throttledIndexPrice);
  const { buys, sells } = perpsOrderbook;
  const minBuyPrice = sells?.at(-1)?.price ?? NaN;
  const maxSellPrice = buys?.at(0)?.price ?? NaN;
  const isPriceOutOfBounds = perpIsBuy
    ? Boolean(minBuyPrice) && limitPriceNum < minBuyPrice
    : Boolean(maxSellPrice) && limitPriceNum > maxSellPrice;

  const prefix = perpIsBuy ? "Max" : "Min";

  if (isPriceOutOfBounds)
    return {
      limitPriceError: `${prefix} price is ${perpIsBuy ? "below" : "above"} best orderbook price.`,
    };

  const priceWithTolerance =
    indexPriceNum * (1 + getMaxSlippageFactor(false) * (perpIsBuy ? 1 : -1));

  const isPriceOutOfTolerance = perpIsBuy
    ? limitPriceNum > priceWithTolerance
    : limitPriceNum < priceWithTolerance;
  const formattedPriceWithTolerance = formatValue(priceWithTolerance);
  const limitPriceError = isPriceOutOfTolerance
    ? `${prefix} limit price should be ${formattedPriceWithTolerance}`
    : "";

  return { limitPriceError };
};
